import {
    TextInput,
    BooleanInput,
    useTranslate,
    required,
    TabbedForm,
    FormTab,
    regex,
    NumberInput
} from 'react-admin'
import { useLocation } from 'react-router-dom'
import { Grid, Typography } from '@mui/material'
import DefaultToolbar from 'components/common/DefaultToolbar'
import AutoCompleteSelectWithData from 'components/formfields/AutoCompleteSelectWithData'
import makeStyles from '@material-ui/styles/makeStyles'
import { ChoiceType } from 'types'
import { useSchoolsSchoolsQuery } from 'apollo/school_manager/queries/SchoolsSchools.generated'
import { useSchoolsSchoolYearsQuery } from 'apollo/school_manager/queries/SchoolsSchoolYears.generated'
import { formStyles } from 'layout/styles/formStyles'
import { FormSpy } from 'react-final-form'
import SchoolClassRepresentativeListAsArrayField from 'resources/schoolClassRepresentative/SchoolClassRepresentativeListAsArrayField'
import SchoolClassTagsInput from './SchoolClassTagsInput'
import SchoolClassEditToolbar from './SchoolClassEditToolbar'
import { Variables } from 'components/variables'

const useStyles = makeStyles(formStyles)

const SchoolClassForm = ({ isPristineForm, setIsPristineFormCallback, ...props }) => {
    const { data: schools = {} } = useSchoolsSchoolsQuery({
        variables: {
            first: 10000
        },
        fetchPolicy: 'cache-and-network' // Set a fetch policy to retrieve newly added data
    })
    const { data: years = {} } = useSchoolsSchoolYearsQuery({ fetchPolicy: 'cache-and-network' })
    const formStyle = useStyles()
    const translate = useTranslate()
    const location = useLocation<{ schoolId?: string }>()
    const { schoolId } = location?.state || {}
    const validateAjatId = regex(/^[\w-]+$/, translate('manager.regex.az09'))

    return (
        <TabbedForm
            {...props}
            toolbar={props.isEdit ? <SchoolClassEditToolbar {...props} /> : <DefaultToolbar />}
            redirect={props.basePath}
        >
            <FormTab label={translate('manager.tabs.general')} path="/">
                <Grid container>
                    <Grid item md={7}>
                        <Grid item md={12}>
                            <Typography variant="h6" gutterBottom>
                                {translate('manager.resources.schoolClass.class')}
                            </Typography>
                        </Grid>
                        <TextInput
                            source="ajat_id"
                            variant="outlined"
                            label={translate('manager.resources.schoolClass.ajat_id')}
                            validate={[required(), validateAjatId]}
                            fullWidth
                            helperText={translate('manager.regex.az09')}
                            disabled={props.isEdit}
                            defaultValue={props.isEdit ? undefined : 'placeholder_id'}
                            className={props.isEdit ? '' : formStyle.hidden}
                        />
                        <TextInput
                            source="name"
                            variant="outlined"
                            label={translate('manager.resources.schoolClass.name')}
                            validate={requiredValidate}
                            fullWidth
                        />
                        <Grid container>
                            <Grid item md={6} paddingRight={1}>
                                <AutoCompleteSelectWithData
                                    defaultValue={schoolId ? Number(schoolId) : undefined}
                                    required
                                    format // For some reason this value needs to be formatted to a string to print the options.
                                    source="school_id"
                                    data={
                                        (schools?.schoolsSchools?.data &&
                                            schools.schoolsSchools.data) ??
                                        []
                                    }
                                    customChoices={
                                        (schools?.schoolsSchools?.data &&
                                            schools.schoolsSchools.data.map(
                                                (item): ChoiceType => ({
                                                    id: item.id,
                                                    name: `${item.name} | ${item?.ajat_id}` ?? ''
                                                })
                                            )) ??
                                        []
                                    }
                                />
                            </Grid>
                            <Grid item md={6} paddingLeft={1}>
                                <AutoCompleteSelectWithData
                                    required
                                    source="school_year_id"
                                    data={
                                        (years?.schoolsSchoolYears?.data &&
                                            years.schoolsSchoolYears.data) ??
                                        []
                                    }
                                />
                            </Grid>
                        </Grid>
                        <TextInput
                            source="embroidery_1"
                            variant="outlined"
                            label={translate('manager.resources.schoolClass.embroidery_1')}
                            fullWidth
                        />
                        <TextInput
                            source="embroidery_2"
                            variant="outlined"
                            label={translate('manager.resources.schoolClass.embroidery_2')}
                            fullWidth
                        />
                        <TextInput
                            source="embroidery_3"
                            variant="outlined"
                            label={translate('manager.resources.schoolClass.embroidery_3')}
                            fullWidth
                        />
                        <NumberInput
                            source="students_amount"
                            variant="outlined"
                            label={translate('manager.resources.schoolClass.students_amount')}
                            fullWidth
                        />
                        <NumberInput
                            source="priority"
                            variant="outlined"
                            label={translate('manager.resources.schoolClass.priority')}
                            fullWidth
                        />
                        {props?.record?.id && (
                            <>
                                <Typography variant="h6" gutterBottom>
                                    {translate('manager.resources.school.tags')}
                                </Typography>
                                <SchoolClassTagsInput schoolClassId={props.record.id} />
                            </>
                        )}
                    </Grid>
                    <Grid item md={1} />
                    <Grid item md={4}>
                        <Grid item md={12} mt={3} mb={3}>
                            <BooleanInput
                                row
                                source="active"
                                label={translate('manager.resources.general.published')}
                                defaultValue={true}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </FormTab>
            {!!props.isEdit && (
                <FormTab label={translate('manager.tabs.contact_persons')} path="contact-persons">
                    <Grid container className={formStyle.fullWidth}>
                        <SchoolClassRepresentativeListAsArrayField {...props} />
                    </Grid>
                </FormTab>
            )}
            <FormTab label={translate('manager.tabs.variables')} path="variables">
                <Variables record={props?.record} entityType="school_class" />
            </FormTab>
            <FormSpy
                subscription={{ pristine: true }}
                onChange={(props) => {
                    setIsPristineFormCallback(props?.pristine)
                }}
            />
        </TabbedForm>
    )
}

const requiredValidate = [required()]

export default SchoolClassForm
